/**
 * Wer einen Einsatzzustand schalten darf — die Rechtetabelle aus `07` §7.8.
 *
 * **Warum sie hier steht und nicht in `packages/sync`.** Der Automat dort
 * sagt es selbst: „Nicht hier: die Rechte. [...] Ob ein Übergang fachlich
 * möglich ist, entscheidet der Automat; ob ihn dieser Benutzer machen darf,
 * entscheidet die Autorisierung." Beides in einer Tabelle zu führen hieße,
 * jede Rechteänderung im Automaten nachzuziehen.
 *
 * Und warum ausgerechnet in `packages/auth`: `01` §1.6 führt Authentifizierung
 * **und** Autorisierung zusammen, und dieses Paket wendet das Rollenmodell an,
 * statt es zu wiederholen. Eine Rechtetabelle ist genau das — angewandtes
 * Rollenmodell. Wenn `packages/domain/jobs` mit `VIK-002` entsteht, kann sie
 * dorthin wandern; bis dahin ist dies der einzige Ort, an dem Portal und
 * Monteur-App sie beide erreichen, ohne dass eine der beiden sie nachbaut.
 *
 * **Die Tabelle stand bis zum 05.09.2026 nirgends im Code.** ADR-045 hat sie
 * am 21.08.2026 eingeführt, drei Dokumente berufen sich darauf, und
 * implementiert war nur der Automat. Solange niemand schreiben konnte, fiel
 * das nicht auf.
 */

import type { Rolle } from "@hwc/contracts";
import type { EinsatzZustand } from "@hwc/sync";

/**
 * Wie jemand einen Übergang auslöst.
 *
 * Der Unterschied ist nicht formal. ADR-045: „Das Büro darf die Übergänge des
 * Monteurs **stellvertretend** setzen — er ruft an, statt zu tippen, und bei
 * schlechtem Empfang ist das der Normalfall. Der Preis ist die Urheberschaft:
 * Ein stellvertretend gesetzter Zustand stammt nicht von dem, der vor Ort
 * war."
 *
 * Deshalb ist `stellvertretend` ein eigener Ausgang und kein `ja` mit
 * Fußnote: Wer so schaltet, muss beide Namen protokollieren (`05` §5.15).
 */
export type Schaltrecht = "ja" | "stellvertretend" | "nein";

export interface Schaltfrage {
  readonly von: EinsatzZustand;
  readonly nach: EinsatzZustand;
  readonly rollen: readonly Rolle[];
  /** Ist der Handelnde der zugewiesene Monteur dieses Einsatzes? */
  readonly istZugewiesen: boolean;
}

/**
 * Die Übergänge, die dem Monteur gehören — Zeile für Zeile aus `07` §7.8.
 *
 * `vor-ort → wartet` und `wartet → vor-ort` stehen dort als ein Eintrag
 * (`VorOrt ↔ Wartet`) und sind hier zwei, weil ein Übergang eine Richtung hat.
 */
const MONTEURWEGE: readonly (readonly [EinsatzZustand, EinsatzZustand])[] = [
  ["geplant", "anfahrt"],
  ["anfahrt", "vor-ort"],
  ["vor-ort", "wartet"],
  ["wartet", "vor-ort"],
  ["vor-ort", "abschluss"],
  ["abschluss", "uebermittelt"],
];

/** Was ausschließlich das Büro darf. */
const BUEROWEGE: readonly (readonly [EinsatzZustand, EinsatzZustand])[] = [
  ["uebermittelt", "geprueft"],
  ["uebermittelt", "abschluss"],
];

/**
 * Rollen, die disponieren dürfen.
 *
 * `07` §7.8 nennt in der Kopfzeile „Büro / Disposition" und „Inhaber". Im
 * Rollenmodell aus `01` §1.6 sind das drei: `office`, `dispatcher` und
 * `tenant-admin`. Die Buchhaltung gehört ausdrücklich nicht dazu — sie sieht
 * nach `07` §7.2 freigegebene Auftragsdaten, sie führt keine Einsätze.
 */
const BUERO: readonly Rolle[] = ["office", "dispatcher", "tenant-admin"];

function passt(
  wege: readonly (readonly [EinsatzZustand, EinsatzZustand])[],
  von: EinsatzZustand,
  nach: EinsatzZustand,
): boolean {
  return wege.some(([a, b]) => a === von && b === nach);
}

function hat(rollen: readonly Rolle[], gesucht: readonly Rolle[]): boolean {
  return gesucht.some((r) => rollen.includes(r));
}

/**
 * Darf dieser Benutzer diesen Übergang auslösen?
 *
 * **Die Frage nach der fachlichen Zulässigkeit stellt sie nicht.** Dafür gibt
 * es `uebergangErlaubt` in `packages/sync`, und beide Antworten werden
 * gebraucht: Ein Übergang kann erlaubt und unmöglich sein (das Büro darf
 * stornieren, aber nicht aus `geprueft` heraus) oder möglich und verboten (der
 * Monteur könnte fachlich prüfen, darf es aber nicht).
 *
 * Die Reihenfolge der Prüfungen ist bewusst: Stornierung zuerst, weil sie für
 * **jeden** Ausgangszustand gilt und sonst durch die Wegetabellen fiele.
 */
export function darfSchalten(frage: Schaltfrage): Schaltrecht {
  const { von, nach, rollen, istZugewiesen } = frage;

  // `* → Storniert`: nur Büro und Inhaber, aus jedem Zustand. Der Monteur
  // storniert nie — ADR-045 lässt die Zeile für ihn leer.
  if (nach === "storniert") {
    return hat(rollen, BUERO) ? "ja" : "nein";
  }

  if (passt(BUEROWEGE, von, nach)) {
    return hat(rollen, BUERO) ? "ja" : "nein";
  }

  if (passt(MONTEURWEGE, von, nach)) {
    // Der zugewiesene Monteur schaltet in eigenem Namen.
    if (istZugewiesen && rollen.includes("technician")) return "ja";
    // Das Büro schaltet für ihn — mit dem Preis, den ADR-045 benennt.
    if (hat(rollen, BUERO)) return "stellvertretend";
    return "nein";
  }

  // Kein bekannter Übergang. Die sichere Richtung ist „nein": Ein Weg, den
  // `07` §7.8 nicht kennt, ist keiner, den irgendeine Rolle gehen darf.
  return "nein";
}

/** Kurzform, wenn nur die Ja/Nein-Frage zählt. */
export function darf(frage: Schaltfrage): boolean {
  return darfSchalten(frage) !== "nein";
}

/**
 * Wer einen Servicebericht freigibt oder zurückgibt — `07` §7.16.
 *
 * Der Bericht hängt am Einsatz, aber sein Zustandsraum ist ein anderer, und
 * die Regel ist einfacher: Erstellen darf, wer den Einsatz gefahren hat;
 * freigeben darf nur das Büro. „Die Freigabe bezieht sich immer auf eine
 * konkrete Version" — das prüft die aufrufende Stelle, nicht diese Funktion.
 */
export function darfBerichtFreigeben(rollen: readonly Rolle[]): boolean {
  return hat(rollen, BUERO);
}

/**
 * Wer einen Eingangsvorgang zuordnen, zurückstellen oder ablehnen darf.
 *
 * `07` §7.2 führt den Eingangskorb unter Büro/Disposition. Der Inhaber kommt
 * dazu, weil er in einem Betrieb dieser Größe regelmäßig selbst am Postfach
 * sitzt — die Personalunion ist dort der Regelfall, nicht die Ausnahme.
 */
export function darfEingangBearbeiten(rollen: readonly Rolle[]): boolean {
  return hat(rollen, BUERO);
}

/**
 * Wer einen Kunden anlegen oder ändern darf.
 *
 * `07` §7.2 führt „Kundenakte" unter Büro/Disposition und „Kunden- und
 * Projektübersicht" beim Inhaber — dieselbe Runde wie beim Eingangskorb.
 *
 * **Wer hier fehlt, fehlt mit Absicht.** Die Buchhaltung sieht nach `07` §7.2
 * „freigegebene Auftragsdaten", nicht den Stamm; der Monteur bekommt die
 * freigegebene Telefonnummer, nicht die Akte. Und der externe Partner — der
 * Steuerberater — hat Leserecht auf einen abgegrenzten Bereich. Ein
 * Kundendatensatz, den vier Rollen ändern können, hat am Ende keine
 * verlässliche Fassung mehr.
 */
export function darfKundenPflegen(rollen: readonly Rolle[]): boolean {
  return hat(rollen, BUERO);
}

/**
 * Wer einen Vorgang aus der Quarantäne freigibt.
 *
 * **Nur der Administrator**, und das ist enger als alles andere hier. ADR-023
 * lässt den Signaturscanner beanstanden; was er beanstandet hat, wieder
 * zugänglich zu machen ist eine Entscheidung mit Folgen für den ganzen Stack.
 * Sie gehört nicht in die tägliche Arbeit am Posteingang.
 */
export function darfQuarantaeneFreigeben(rollen: readonly Rolle[]): boolean {
  return rollen.includes("tenant-admin");
}
